import React, { useEffect, useState, useCallback } from 'react';
import { getPlantingCycleDetails } from '../../../services/v2/plantingCycleService';
import CycleStatus from './CycleStatus';
import GddDliAccumulatedChart from './GddDliAccumulatedChart';
import HarvestHistoryList from './HarvestHistoryList';
import LoadingComponent from '../../loading/LoadingComponent';
import { Card } from 'react-bootstrap';

const CycleDetailView = ({ cycleId, onBack, readOnly = false }) => {
    const [cycleDetails, setCycleDetails] = useState(null);
    const [loading, setLoading] = useState(true);

    const fetchDetails = useCallback(async () => {
        setLoading(true);
        try {
            const data = await getPlantingCycleDetails(cycleId);
            setCycleDetails(data);
        } catch (error) {
            console.error("Error fetching cycle details", error);
        } finally {
            setLoading(false);
        }
    }, [cycleId]);

    useEffect(() => {
        fetchDetails();
    }, [fetchDetails]);

    const renderProgressChips = (phase, isCompleted) => {
        switch (phase.completionMetric) {
            case 'CHILL':
                return (
                    <div className="bg-blue-50 border border-blue-100 px-3 py-1.5 rounded-lg">
                        <div className="text-[9px] uppercase font-bold text-blue-400">Frío Alcanzado (CHP)</div>
                        <div className="text-xs font-bold text-blue-700">
                            {Math.round(isCompleted ? phase.chillAtCompletion || 0 : cycleDetails.chillAccumulated || 0)} / {phase.targetChill ?? '—'}
                        </div>
                    </div>
                );
            case 'DAYS':
            case 'MANUAL':
                return (
                    <div className="bg-gray-50 border border-gray-200 px-3 py-1.5 rounded-lg">
                        <div className="text-[9px] uppercase font-bold text-gray-400">
                            {phase.completionMetric === 'MANUAL' ? 'Cierre Manual' : 'Días Transcurridos'}
                        </div>
                        <div className="text-xs font-bold text-gray-700">
                            {phase.completionMetric === 'MANUAL'
                                ? 'Requiere forzar etapa'
                                : `${cycleDetails.daysCount || 0} / ${phase.expectedDaysSnapshot ?? '—'} días`}
                        </div>
                    </div>
                );
            default:
                return (
                    <>
                        <div className="bg-orange-50 border border-orange-100 px-3 py-1.5 rounded-lg">
                            <div className="text-[9px] uppercase font-bold text-orange-400">GDD Alcanzado</div>
                            <div className="text-xs font-bold text-orange-700">
                                {Math.round(isCompleted ? phase.gddAtCompletion : cycleDetails.gddAccumulated)} / {phase.targetGdd}
                            </div>
                        </div>
                        <div className="bg-yellow-50 border border-yellow-100 px-3 py-1.5 rounded-lg">
                            <div className="text-[9px] uppercase font-bold text-yellow-500">DLI Promedio Diario</div>
                            <div className="text-xs font-bold text-yellow-700">
                                {phase.avgDliPerDay != null ? phase.avgDliPerDay.toFixed(1) : '—'} / {phase.targetDli}
                            </div>
                        </div>
                    </>
                );
        }
    };

    const renderObjectiveChips = (phase) => {
        switch (phase.completionMetric) {
            case 'CHILL':
                return (
                    <div className="bg-blue-50 border border-blue-100 px-3 py-1.5 rounded-lg">
                        <div className="text-[9px] uppercase font-bold text-blue-400">Frío Objetivo (CHP)</div>
                        <div className="text-xs font-bold text-blue-700">{phase.targetChill ?? '—'}</div>
                    </div>
                );
            case 'DAYS':
                return (
                    <div className="bg-gray-50 border border-gray-200 px-3 py-1.5 rounded-lg">
                        <div className="text-[9px] uppercase font-bold text-gray-400">Días Objetivo</div>
                        <div className="text-xs font-bold text-gray-700">{phase.expectedDaysSnapshot ?? '—'}</div>
                    </div>
                );
            case 'MANUAL':
                return (
                    <div className="bg-gray-50 border border-gray-200 px-3 py-1.5 rounded-lg">
                        <div className="text-[9px] uppercase font-bold text-gray-400">Cierre</div>
                        <div className="text-xs font-bold text-gray-700">Manual</div>
                    </div>
                );
            default:
                return (
                    <>
                        <div className="bg-orange-50 border border-orange-100 px-3 py-1.5 rounded-lg">
                            <div className="text-[9px] uppercase font-bold text-orange-400">GDD Objetivo</div>
                            <div className="text-xs font-bold text-orange-700">{phase.targetGdd}</div>
                        </div>
                        <div className="bg-yellow-50 border border-yellow-100 px-3 py-1.5 rounded-lg">
                            <div className="text-[9px] uppercase font-bold text-yellow-500">DLI Objetivo (prom. diario)</div>
                            <div className="text-xs font-bold text-yellow-700">{phase.targetDli}</div>
                        </div>
                    </>
                );
        }
    };

    if (loading) return <LoadingComponent />;
    if (!cycleDetails) return <div>No se pudo cargar el detalle del ciclo.</div>;

    return (
        <div className="space-y-8 animate-fade-in">
            {/* Action Bar */}
            <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
                <button
                    onClick={onBack}
                    className="flex items-center text-green-700 hover:text-green-800 font-medium transition-colors bg-green-50 px-3 py-1.5 rounded-lg border border-green-100 shadow-sm"
                >
                    <span className="material-icons-outlined mr-1 text-lg">arrow_back</span>
                    Volver a la lista de ciclos
                </button>
                <h4 className="mb-0 text-gray-800 font-bold">
                    {cycleDetails.name?.trim() ? cycleDetails.name : `Ciclo #${cycleDetails.id}`}
                </h4>
            </div>

            {/* Cycle Biological Status (Premium Dashboard) */}
            <CycleStatus plantingCycle={cycleDetails} onCycleUpdated={fetchDetails} readOnly={readOnly} />

            {/* Harvest History */}
            <HarvestHistoryList harvests={cycleDetails.harvests} />

            {/* Detailed Phenological History */}
            <Card className="border-0 shadow-sm rounded-xl overflow-hidden">
                <Card.Header className="bg-white border-b border-gray-100 py-4 flex flex-wrap items-center justify-between gap-3">
                    <div>
                        <h5 className="mb-0 text-gray-800 font-bold flex items-center">
                            <span className="material-icons-outlined mr-2 text-green-600">timeline</span>
                            Historial de Etapas
                        </h5>
                        <p className="text-xs text-gray-400 mb-0 mt-1 ml-7">Seguimiento del desarrollo de tu cultivo</p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                        <span className="flex items-center gap-1 px-2.5 py-1 bg-green-50 border border-green-100 rounded-full text-[10px] font-bold text-green-700">
                            <span className="material-icons text-xs">check_circle</span>
                            Completada
                        </span>
                        <span className="flex items-center gap-1 px-2.5 py-1 bg-white border border-gray-200 rounded-full text-[10px] font-bold text-gray-600">
                            <span className="material-icons-outlined text-xs">radio_button_checked</span>
                            En curso
                        </span>
                        <span className="flex items-center gap-1 px-2.5 py-1 bg-gray-50 border border-gray-100 rounded-full text-[10px] font-bold text-gray-400">
                            <span className="material-icons-outlined text-xs">schedule</span>
                            Proyectada
                        </span>
                    </div>
                </Card.Header>

                {(() => {
                    const sortedPhaseLogs = (cycleDetails.phaseLogs || [])
                        .slice()
                        .sort((a, b) => (a.sequenceOrder || 0) - (b.sequenceOrder || 0));

                    const statusBadge = (isCompleted, isCurrent, isSkipped) => (
                        <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-black uppercase
                            ${isCompleted ? 'bg-green-50 text-green-700' : isCurrent ? 'bg-green-50 text-green-700' : isSkipped ? 'bg-gray-100 text-gray-500' : 'bg-gray-100 text-gray-400'}
                        `}>
                            <span className="material-icons text-[13px]">
                                {isCompleted ? 'check_circle' : isCurrent ? 'radio_button_checked' : isSkipped ? 'block' : 'schedule'}
                            </span>
                            {isCompleted ? 'Completada' : isCurrent ? 'En curso' : isSkipped ? 'Omitida' : 'Proyectada'}
                        </span>
                    );

                    const dateColumn = (phase, isCompleted, isCurrent, isSkipped) => {
                        if (isCompleted || isSkipped) {
                            return (
                                <div className="flex items-start gap-2">
                                    <span className="material-icons-outlined text-gray-300 text-base mt-0.5">event_available</span>
                                    <div>
                                        <div className="text-[9px] uppercase font-bold text-gray-400">
                                            {isSkipped ? 'Cierre anticipado' : 'Fecha alcanzada'}
                                        </div>
                                        <div className="text-xs font-bold text-green-700">{phase.actualCompletionDate || '---'}</div>
                                    </div>
                                </div>
                            );
                        }
                        return (
                            <div className="space-y-1.5">
                                {phase.estimatedCompletionDate && (
                                    <div className="flex items-start gap-2">
                                        <span className="material-icons-outlined text-indigo-300 text-base mt-0.5">event</span>
                                        <div>
                                            <div className="text-[9px] uppercase font-bold text-gray-400">Fecha estimada</div>
                                            <div className="text-xs font-bold text-indigo-700">{phase.estimatedCompletionDate}</div>
                                        </div>
                                    </div>
                                )}
                                {phase.estimatedCompletionDateMin && phase.estimatedCompletionDateMax && (
                                    <div className="flex items-start gap-2">
                                        <span className="material-icons-outlined text-gray-300 text-base mt-0.5">date_range</span>
                                        <div>
                                            <div className="text-[9px] uppercase font-bold text-gray-400">Rango estimado</div>
                                            <div className="text-xs font-bold text-gray-600">
                                                {phase.estimatedCompletionDateMin} — {phase.estimatedCompletionDateMax}
                                            </div>
                                        </div>
                                    </div>
                                )}
                                {phase.estimatedCompletionDateByDays &&
                                    phase.estimatedCompletionDateByDays !== phase.estimatedCompletionDate && (
                                    <div className="flex items-start gap-2">
                                        <span className="material-icons-outlined text-amber-400 text-base mt-0.5">history_toggle_off</span>
                                        <div>
                                            <div className="text-[9px] uppercase font-bold text-gray-400">Estimación por días (desde hoy)</div>
                                            <div className="text-xs font-bold text-amber-600">{phase.estimatedCompletionDateByDays}</div>
                                        </div>
                                    </div>
                                )}
                                {!phase.estimatedCompletionDate && !isCompleted && !isSkipped && (
                                    <span className="text-xs text-gray-300">—</span>
                                )}
                            </div>
                        );
                    };

                    return (
                        <>
                            {/* Desktop: tabla */}
                            <div className="hidden md:block overflow-x-auto">
                                <table className="w-full text-left border-collapse">
                                    <thead>
                                        <tr className="bg-gray-50 text-[10px] uppercase font-bold text-gray-400 tracking-wider">
                                            <th className="py-3 px-6">Etapa Fenológica</th>
                                            <th className="py-3 px-4">Estado</th>
                                            <th className="py-3 px-4">Indicadores</th>
                                            <th className="py-3 px-6">Fecha de Cierre</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {sortedPhaseLogs.map((phase, index) => {
                                            const isCompleted = phase.status === 'COMPLETED';
                                            const isCurrent = phase.status === 'IN_PROGRESS';
                                            const isSkipped = phase.status === 'SKIPPED';
                                            const isFuture = !isCompleted && !isCurrent && !isSkipped;

                                            return (
                                                <tr key={phase.id} className="border-b border-gray-50 last:border-0 align-top hover:bg-gray-50/50 transition-colors">
                                                    <td className="py-4 px-6">
                                                        <div className="flex items-start gap-3">
                                                            <div className={`w-8 h-8 rounded-full flex items-center justify-center shadow-sm shrink-0
                                                                ${isCompleted ? 'bg-green-500 text-white' : isCurrent ? 'bg-white border-2 border-green-500 text-green-600' : isSkipped ? 'bg-gray-300 text-white' : 'bg-gray-100 text-gray-300'}
                                                            `}>
                                                                {isCompleted
                                                                    ? <span className="material-icons text-sm">check</span>
                                                                    : isSkipped
                                                                        ? <span className="material-icons text-sm">block</span>
                                                                        : <span className="text-xs font-bold">{index + 1}</span>
                                                                }
                                                            </div>
                                                            <div>
                                                                <div className="flex items-center gap-2">
                                                                    <h6 className={`font-bold mb-0 ${isCompleted ? 'text-gray-700' : isCurrent ? 'text-green-700' : isSkipped ? 'text-gray-500' : 'text-gray-400'}`}>
                                                                        {index + 1}. {phase.phaseName}
                                                                    </h6>
                                                                    {isCurrent && (
                                                                        <span className="px-2 py-0.5 bg-green-100 text-green-700 text-[9px] font-black rounded uppercase">Actual</span>
                                                                    )}
                                                                </div>
                                                                {isSkipped && (
                                                                    <p className="text-[10px] text-gray-400 mb-0 mt-0.5">Omitida — cierre anticipado del ciclo</p>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="py-4 px-4">{statusBadge(isCompleted, isCurrent, isSkipped)}</td>
                                                    <td className="py-4 px-4">
                                                        <div className="flex flex-wrap gap-2">
                                                            {(isCompleted || isCurrent) && renderProgressChips(phase, isCompleted)}
                                                            {isFuture && renderObjectiveChips(phase)}
                                                            {isSkipped && <span className="text-xs text-gray-300">—</span>}
                                                        </div>
                                                    </td>
                                                    <td className="py-4 px-6">{dateColumn(phase, isCompleted, isCurrent, isSkipped)}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>

                            {/* Mobile: tarjetas apiladas (mismo lenguaje visual que la tabla de desktop) */}
                            <div className="md:hidden p-4 space-y-3">
                                {sortedPhaseLogs.map((phase, index) => {
                                    const isCompleted = phase.status === 'COMPLETED';
                                    const isCurrent = phase.status === 'IN_PROGRESS';
                                    const isSkipped = phase.status === 'SKIPPED';
                                    const isFuture = !isCompleted && !isCurrent && !isSkipped;

                                    return (
                                        <div key={phase.id} className="bg-gray-50 border border-gray-100 rounded-lg p-4 space-y-3">
                                            <div className="flex items-start justify-between gap-3">
                                                <div className="flex items-start gap-3">
                                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center shadow-sm shrink-0
                                                        ${isCompleted ? 'bg-green-500 text-white' : isCurrent ? 'bg-white border-2 border-green-500 text-green-600' : isSkipped ? 'bg-gray-300 text-white' : 'bg-gray-100 text-gray-300'}
                                                    `}>
                                                        {isCompleted
                                                            ? <span className="material-icons text-sm">check</span>
                                                            : isSkipped
                                                                ? <span className="material-icons text-sm">block</span>
                                                                : <span className="text-xs font-bold">{index + 1}</span>
                                                        }
                                                    </div>
                                                    <div>
                                                        <div className="flex items-center gap-2 flex-wrap">
                                                            <h6 className={`font-bold mb-0 ${isCompleted ? 'text-gray-700' : isCurrent ? 'text-green-700' : isSkipped ? 'text-gray-500' : 'text-gray-400'}`}>
                                                                {index + 1}. {phase.phaseName}
                                                            </h6>
                                                            {isCurrent && (
                                                                <span className="px-2 py-0.5 bg-green-100 text-green-700 text-[9px] font-black rounded uppercase">Actual</span>
                                                            )}
                                                        </div>
                                                        {isSkipped && (
                                                            <p className="text-[10px] text-gray-400 mb-0 mt-0.5">Omitida — cierre anticipado del ciclo</p>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>

                                            {statusBadge(isCompleted, isCurrent, isSkipped)}

                                            <div className="flex flex-wrap gap-2">
                                                {(isCompleted || isCurrent) && renderProgressChips(phase, isCompleted)}
                                                {isFuture && renderObjectiveChips(phase)}
                                            </div>

                                            <div className="pt-2 border-t border-gray-100">
                                                <div className="text-[9px] uppercase font-black text-gray-400 tracking-wider mb-2">Fecha de Cierre</div>
                                                {dateColumn(phase, isCompleted, isCurrent, isSkipped)}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </>
                    );
                })()}
            </Card>

            {/* Charts Section */}
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <h5 className="mb-6 text-gray-800 font-bold flex items-center">
                    <span className="material-icons-outlined mr-2 text-indigo-600">trending_up</span>
                    Avance Acumulado (GDD) y Luz Diaria (DLI)
                </h5>
                <GddDliAccumulatedChart plantingCycle={cycleDetails} />
            </div>
        </div>
    );
};

export default CycleDetailView;